import { useEffect, useState } from "react";

import { Alert } from "../base/Alert";
import Card from "../shared/Card/Card";
import MiniTable from "../base/MiniTable";
import LoadingWheel from "../shared/LoadingWheel";
import { errorResolver } from "../../utils/errorResolver";
import {
  ICustomerIntegration,
  IntegrationServiceLookup,
} from "../../models/CustomerIntegration/CustomerIntegration";
import useCustomerIntegrations from "../../data/CustomerIntegration/useCustomerIntegrations";
import date from "date-and-time";
import { useUserLookup } from "../../hooks/useUserLookup";

interface CustomerIntegrationListProps {
  customerId: string;
}

export function CustomerIntegrationList({
  customerId,
}: CustomerIntegrationListProps) {
  const { customerIntegrations } = useCustomerIntegrations(customerId);
  const { isLoading: userLookupLoading, lookupUser } =
    useUserLookup(customerId);
  const [pageNumber, setPageNumber] = useState(0);
  const [itemsPerPage] = useState(10);
  const [numPages, setNumPages] = useState(0);
  const [integrations, setIntegrations] = useState<ICustomerIntegration[]>([]);
  const [loading, setLoading] = useState(true);

  const setTableData = (pageNo: number, perPage: number) => {
    if (
      customerIntegrations.data === null ||
      customerIntegrations.data === undefined
    )
      return;

    setLoading(true);

    let integrations = Object.keys(IntegrationServiceLookup).map((service) =>
      customerIntegrations.data.find((i) => i.service === parseInt(service))
    );

    const filteredIntegrations = integrations.filter(
      (i) => i !== undefined
    ) as ICustomerIntegration[];

    let startIndex = pageNo * perPage;
    let endIndex = startIndex + perPage;

    setIntegrations(filteredIntegrations.slice(startIndex, endIndex));
    setLoading(false);
  };

  const handleNextPage = () => {
    let newPage = pageNumber + 1;

    if (newPage > numPages) return;

    setPageNumber(newPage);
    setTableData(newPage, itemsPerPage);
  };

  const handlePrevPage = () => {
    let newPage = pageNumber - 1;

    if (newPage < 0) return;

    setPageNumber(newPage);
    setTableData(newPage, itemsPerPage);
  };

  useEffect(() => {
    if (
      !customerIntegrations.isLoading &&
      customerIntegrations.data &&
      customerIntegrations.data.length > 0
    ) {
      setNumPages(Math.ceil(customerIntegrations.data.length / itemsPerPage));

      setTableData(pageNumber, itemsPerPage);
    } else if (!customerIntegrations.isLoading && customerIntegrations.data) {
      setIntegrations([]);
      setLoading(false);
    }
  }, [customerIntegrations.isLoading, customerIntegrations.data]);

  return (
    <>
      <Card title="Integrations" bodyClassName="p-4">
        {customerIntegrations.isLoading || userLookupLoading || loading ? (
          <div className="py-4">
            <LoadingWheel />
          </div>
        ) : customerIntegrations.isError ? (
          <div className="p-4">
            <Alert
              severity="error"
              title="Error getting customer integrations"
              message={errorResolver(customerIntegrations.error)}
            />
          </div>
        ) : (
          <>
            <MiniTable>
              <MiniTable.Header>
                <MiniTable.HeaderCell>Service</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>Expiry</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>Authorised By</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>Authorised On</MiniTable.HeaderCell>
              </MiniTable.Header>
              <MiniTable.Body>
                {integrations.length > 0 ? (
                  integrations.map((i, j) => {
                    const user = lookupUser(i.userId);

                    return (
                      <MiniTable.Row key={j}>
                        <MiniTable.Cell>
                          {IntegrationServiceLookup[i.service]}
                        </MiniTable.Cell>
                        <MiniTable.Cell>
                          {date.format(
                            new Date(i.expiry),
                            "HH:mm:ss DD/MM/YYYY"
                          )}

                          <span className="text-gray-600 italic">{` (${Math.ceil(
                            (new Date(i.expiry).getTime() -
                              new Date().getTime()) /
                              (1000 * 60 * 60 * 24)
                          )} days)`}</span>
                        </MiniTable.Cell>
                        <MiniTable.Cell>
                          {!user ? (
                            <span className="italic text-gray-600">
                              Not found
                            </span>
                          ) : (
                            `${user.forename} ${user.surname}`
                          )}
                        </MiniTable.Cell>
                        <MiniTable.Cell>
                          {date.format(
                            new Date(i.created),
                            "HH:mm:ss DD/MM/YYYY"
                          )}
                        </MiniTable.Cell>
                      </MiniTable.Row>
                    );
                  })
                ) : (
                  <MiniTable.Row>
                    <MiniTable.Cell colSpan={4} className="py-4 text-center">
                      No integrations
                    </MiniTable.Cell>
                  </MiniTable.Row>
                )}
              </MiniTable.Body>
            </MiniTable>
            <MiniTable.Pagination
              totalRows={customerIntegrations.data?.length ?? 0}
              totalPages={numPages}
              currentPage={pageNumber + 1}
              itemsPerPage={itemsPerPage}
              onPrevClick={handlePrevPage}
              onNextClick={handleNextPage}
            />
          </>
        )}
      </Card>
    </>
  );
}
